/**
 * Soft≠Live Page180 base + Page224 dual residual (roster_plane ∥ command_plane).
 * Mount: /ops/dispatch (panel tab · ops-cmd glass)
 *
 * Live buses (no Soft substitute on production):
 *   GET /ops/dispatch/status     always-on posture
 *   GET /personnel               roster_plane · people available to send
 *   GET /responder-dispatches    missions for admin/staff (BE gated)
 *   GET /responder-dispatches/mine  missions for user+ own queue
 * Soft theatre remains host-local only and never claims Live.
 *
 * Soft residual closed:
 *  - voiding Live register with empty Soft-only roster (overviewDispatchRosterOf)
 *  - fixed edge invent as “dispatchable”
 *  - raw API / path paint in operator DOM
 *  - engineer dual re-read · Live toast
 */
import { useMemo, useState } from "react";
import { html, Ico, Alert, go, hashSearchParams, handoffKnownParamsOf } from "./ui.js";
import { path, useApi } from "./api.js";
import { isLocalHost } from "./pathseal.js";
import { overviewDispatchRosterOf } from "./ops-overview-desk.js";
import { boardHeadingOf, operatorSay } from "./operator-say.js";

const SEND_TABS = [
  { id: "people", label: "People", long: "People roster" },
  { id: "missions", label: "Runs", long: "Open runs" },
];

const OPEN_MISSION = new Set([
  "pending", "accepted", "en_route", "enroute", "on_scene", "onscene", "assigned", "active",
]);

/** Soft≠Live: missing contract ops must not crash the desk. */
function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__ops_dispatch_status") return "/ops/dispatch/status";
    if (op === "get__personnel") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/personnel?${parts.join("&")}` : "/personnel";
    }
    if (op === "get__responder-dispatches") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length
        ? `/responder-dispatches?${parts.join("&")}`
        : "/responder-dispatches";
    }
    if (op === "get__responder-dispatches_mine") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length
        ? `/responder-dispatches/mine?${parts.join("&")}`
        : "/responder-dispatches/mine";
    }
    if (op === "get__devices") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/devices?${parts.join("&")}` : "/devices";
    }
    return "";
  }
}

/** Operator-facing bus errors — never paths or stacks. */
export function softDispatchErrorOf(err) {
  const n = String(err == null ? "" : err).trim().toLowerCase();
  if (!n) return "That send action could not be completed.";
  if (/\b403\b|forbidden|insufficient|not_authorized|scope|tenant_scope/.test(n)) {
    return "Your account cannot do that on Send. Ask a site manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(n)) {
    return "That roster or run is not available.";
  }
  if (/\b429\b|too many/.test(n)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/network|failed to fetch|timeout|aborted/.test(n)) {
    return "Network issue — try Refresh.";
  }
  if (/\/ops\/|\/personnel|\/responder|\/devices|http|get |post |status_path|sql|traceback/.test(n)) {
    return "That send action could not be completed.";
  }
  return "That send action could not be completed.";
}

export function softDispatchSeatLabelOf(role) {
  const r = String(role || "").toLowerCase();
  if (r === "user") return "Operator";
  if (r === "admin") return "Site manager";
  if (r === "superadmin") return "Platform";
  if (r === "sa_staff") return "Staff";
  return role || "Seat";
}

/** Pure: dual seat from Live dispatch posture (Page224). */
export function opsDispatchSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const max = Number(lim.roster_max);
  const def = Number(lim.roster_default);
  const peopleMax = Number(lim.personnel_paint_max);
  const missionMax = Number(lim.missions_paint_max);
  const rosterAllowed = (p.roster_plane_allowed === true || p.register_allowed === true);
  const commandAllowed = (p.command_plane_allowed === true || p.unit_select_allowed === true);
  const personnelAllowed = p.personnel_allowed != null
    ? (p.personnel_allowed === true)
    : rosterAllowed;
  const missionsFleet = (p.missions_fleet_allowed === true);
  /* Fail-closed: missing missions_mine must not invent open bus. */
  const missionsMine = (p.missions_mine_allowed === true);
  const missionsAllowed = p.missions_plane_allowed != null
    ? (p.missions_plane_allowed === true)
    : (missionsFleet || missionsMine);
  return {
    registerAllowed: rosterAllowed,
    unitSelectAllowed: commandAllowed,
    personnelAllowed,
    missionsAllowed,
    missionsFleetAllowed: missionsFleet,
    missionsMineAllowed: missionsMine,
    selectedTenantRequired: (p.selected_tenant_required === true),
    reReadAllowed: (p.re_read_allowed === true),
    softTheatreLocalOnly: p.soft_theatre_local_only === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    rosterMax:
      Number.isFinite(max) && max > 0
        ? Math.min(max, 2000)
        : Number.isFinite(def) && def > 0
          ? def
          : 500,
    personnelPaintMax:
      Number.isFinite(peopleMax) && peopleMax > 0 ? Math.floor(peopleMax) : 80,
    missionsPaintMax:
      Number.isFinite(missionMax) && missionMax > 0 ? Math.floor(missionMax) : 40,
  };
}

/** Pure: bound roster paint for 100k clamps — no Soft fill beyond source rows. */
export function dispatchRosterClampOf(rows, maxN) {
  const list = Array.isArray(rows) ? rows : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 500;
  return list.slice(0, cap);
}

/** Pure: map Live /personnel items → send roster rows. */
export function livePersonnelRosterOf(pack, paintMax) {
  const items = (pack && (pack.items || pack.personnel)) || [];
  if (!Array.isArray(items)) return [];
  const cap = Number.isFinite(Number(paintMax)) && Number(paintMax) > 0
    ? Math.floor(Number(paintMax))
    : 80;
  const rows = items.slice(0, cap).map((p, i) => {
    const u = String((p && p.username) || "").trim();
    if (!u) return null;
    const asg = p.assignment && typeof p.assignment === "object" ? p.assignment : null;
    const st = String((asg && asg.status) || p.status || "active").toLowerCase();
    const busy = !!(asg && asg.dispatch_id)
      && !["cancelled", "resolved", "closed", "done"].includes(st);
    const pos = p.position && typeof p.position === "object" ? p.position : null;
    const hasPos = pos
      && Number.isFinite(Number(pos.lat))
      && Number.isFinite(Number(pos.lng));
    const how = busy
      ? (operatorSay(st, "assigned"))
      : (hasPos ? "ready" : "standby");
    return {
      id: u,
      label: u,
      kind: "person",
      kindWord: "Person",
      free: !busy,
      how,
      meta: busy
        ? ((asg && asg.device_id) ? ("run · " + boardHeadingOf(asg.device_id)) : "on run")
        : (hasPos ? "on map" : "no map pin"),
      tone: "",
      soft: false,
      mobile: true,
      live: true,
      onCall: !!(p.on_call),
      consent: String(p.consent || "").trim(),
    };
  }).filter(Boolean);
  rows.sort((a, b) => Number(b.free) - Number(a.free) || a.label.localeCompare(b.label));
  return rows;
}

/** Pure: map Live responder-dispatches → mission rows. */
export function liveMissionRowsOf(pack, paintMax) {
  const items = (pack && (pack.items || pack.dispatches || pack.rows)) || [];
  if (!Array.isArray(items)) return [];
  const cap = Number.isFinite(Number(paintMax)) && Number(paintMax) > 0
    ? Math.floor(Number(paintMax))
    : 40;
  return items.slice(0, cap).map((m, i) => {
    const id = String((m && (m.dispatch_id || m.id)) || ("m" + i)).trim();
    const st = String((m && m.status) || "").toLowerCase().replace(/\s+/g, "_");
    const open = OPEN_MISSION.has(st) || (!st && !(m && m.resolved_at));
    const who = String((m && m.assigned_responder) || "").trim() || "—";
    const dev = String((m && m.device_id) || "").trim();
    const reason = String((m && m.dispatch_reason) || "").trim().slice(0, 80);
    return {
      id,
      status: st || "—",
      open,
      who,
      device: dev,
      reason: reason || "—",
      risk: Number(m && m.risk_score != null ? m.risk_score : 0),
    };
  });
}

function focusSoftUnit(id) {
  try {
    window.dispatchEvent(new CustomEvent("croc-soft-focus", { detail: { id } }));
  } catch {
    /* */
  }
}

/** Full-height dispatchable roster — Live people + Live runs · Soft local optional. */
export function OpsDispatchDesk({ onSelect, onPerson, toast, fleet }) {
  const posture = useApi(safePath("get__ops_dispatch_status"));
  const post = posture.data || {};
  const seat = opsDispatchSeatOf(
    posture.loading || !posture.data
      ? {
          roster_plane_allowed: false,
          command_plane_allowed: false,
          register_allowed: false,
          unit_select_allowed: false,
          personnel_allowed: false,
          missions_plane_allowed: false,
          missions_fleet_allowed: false,
          missions_mine_allowed: false,
          soft_theatre_local_only: true,
          re_read_allowed: false,
          limits: {
            roster_default: 500,
            roster_max: 2000,
            personnel_paint_max: 80,
            missions_paint_max: 40,
          },
        }
      : post,
  );
  const [tab, setTab] = useState("people");
  const localSoft = isLocalHost() && seat.softTheatreLocalOnly;
  const softState = localSoft && fleet && fleet.enabled ? fleet.state : null;
  const handoff = handoffKnownParamsOf(hashSearchParams());
  const handoffDevice = String(handoff.device || "").trim();
  const handoffOwner = String(handoff.owner || "").trim();
  const staffNeedsOwner = seat.selectedTenantRequired === true;
  const fleetOwnerReady = !staffNeedsOwner || !!handoffOwner;

  const peopleR = useApi(
    seat.personnelAllowed
      ? safePath("get__personnel", { query: { limit: seat.personnelPaintMax } })
      : "",
  );
  const fleetMissionsR = useApi(
    seat.missionsFleetAllowed && fleetOwnerReady
      ? safePath("get__responder-dispatches", {
        query: {
          limit: seat.missionsPaintMax,
          ...(handoffOwner ? { owner_admin: handoffOwner } : {}),
        },
      })
      : "",
  );
  const mineMissionsR = useApi(
    seat.missionsMineAllowed && !seat.missionsFleetAllowed
      ? safePath("get__responder-dispatches_mine", {
        query: { limit: seat.missionsPaintMax },
      })
      : "",
  );

  const softRoster = useMemo(
    () => (softState
      ? dispatchRosterClampOf(
        overviewDispatchRosterOf(softState, [], []),
        seat.rosterMax,
      )
      : []),
    [softState, seat.rosterMax],
  );
  const livePeople = useMemo(
    () => (seat.personnelAllowed
      ? livePersonnelRosterOf(peopleR.data, seat.personnelPaintMax)
      : []),
    [peopleR.data, seat.personnelAllowed, seat.personnelPaintMax],
  );
  /** Soft rows only on localhost; Live people always primary on production. */
  const roster = livePeople.length
    ? livePeople
    : softRoster;
  const peopleSource = livePeople.length ? "live" : (softRoster.length ? "soft" : "empty");
  const missionPack = seat.missionsFleetAllowed
    ? fleetMissionsR
    : mineMissionsR;
  const missions = useMemo(
    () => (seat.missionsAllowed
      ? liveMissionRowsOf(missionPack.data, seat.missionsPaintMax)
      : []),
    [missionPack.data, seat.missionsAllowed, seat.missionsPaintMax],
  );
  const openMissions = missions.filter((m) => m.open);
  const ready = roster.filter((r) => r.free);
  const busy = roster.filter((r) => !r.free);
  const bothSealed = !seat.personnelAllowed && !seat.missionsAllowed
    && !seat.registerAllowed && !seat.unitSelectAllowed;
  const roleLabel = softDispatchSeatLabelOf(seat.role || post.principal_role || "");

  const reRead = () => {
    if (!seat.reReadAllowed && !posture.loading) {
      toast && toast("Refresh is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    peopleR.reload && peopleR.reload();
    fleetMissionsR.reload && fleetMissionsR.reload();
    mineMissionsR.reload && mineMissionsR.reload();
    toast && toast("Send refreshed", "go");
  };

  const peopleDenied = !!(peopleR.error
    && /\b40[13]\b|forbidden|insufficient/i.test(peopleR.error || ""));
  const missionDenied = !!(missionPack.error
    && /\b40[13]\b|forbidden|insufficient|not_authorized/i.test(missionPack.error || ""));

  const pulse = posture.loading
    ? "Checking access…"
    : !seat.enabled || bothSealed
      ? "Seat closed"
      : peopleSource === "live"
        ? `${ready.length} free · ${busy.length} on run · ${openMissions.length} open runs`
        : softState
          ? `Lab only · ${ready.length} ready · site roster empty`
          : (peopleR.loading && !peopleR.data)
            ? "Loading people…"
            : "No people on the roster yet · open Run on this site when practice mode is available";

  if (!posture.loading && (post && post.ok === false || bothSealed)) {
    return html`<div
      className="ops-hud ops-send ops-field ops-field--send ops-dispatch ops-cmd"
      data-cmc-residual="page224-dispatch"
      data-field="v555"
      data-soft="0">
      <header className="ops-hud__top">
        <div className="ops-hud__brand">
          <span className="ops-hud__chip">Send</span>
          <span className="ops-hud__chip ops-hud__chip--live">Closed</span>
        </div>
        <button type="button" className="ops-hud__refresh"
          disabled=${!seat.reReadAllowed || posture.loading}
          onClick=${reRead} title="Refresh">
          <${Ico} d="refresh" w=${13} />
        </button>
      </header>
      <p className="ops-hud__pulse">Seat closed</p>
      <p className="ops-hud__quiet">Send is closed for ${roleLabel}.</p>
    </div>`;
  }

  const readyV = !seat.personnelAllowed && !softState ? "—" : ready.length;
  const outV = !seat.personnelAllowed && !softState ? "—" : busy.length;
  const totalV = !seat.personnelAllowed && !softState
    ? "—"
    : String(roster.length);
  const runsV = !seat.missionsAllowed
    ? "—"
    : (missionPack.loading && !missionPack.data ? "…" : openMissions.length);

  return html`<div
    className="ops-hud ops-send ops-field ops-field--send ops-dispatch ops-cmd"
    data-cmc-residual="page224-dispatch"
    data-field="v555"
    data-soft=${peopleSource === "soft" ? "1" : "0"}>

    <div className="ops-hud__scan" aria-hidden="true" />

    <header className="ops-hud__top">
      <div className="ops-hud__brand">
        <span className="ops-hud__chip">Send</span>
        ${peopleSource === "soft"
          ? html`<span className="ops-hud__chip ops-hud__chip--soft">Practice · local</span>`
          : html`<span className="ops-hud__chip ops-hud__chip--live">On site</span>`}
      </div>
      <button
        type="button"
        className="ops-hud__refresh"
        title="Refresh"
        disabled=${!seat.reReadAllowed && !posture.loading}
        onClick=${reRead}>
        <${Ico} d="refresh" w=${13} />
      </button>
    </header>

    <p className="ops-hud__pulse" title="Send status">${pulse}</p>
    ${handoffDevice
      ? html`<${Alert} tone="info" title="Handoff device" data-cmc-handoff="device"
          say=${"Target " + handoffDevice + " was passed in. Confirm before sending — Send does not auto-dispatch."}/>`
      : null}
    ${staffNeedsOwner && !handoffOwner
      ? html`<${Alert} tone="info" title="Tenant scope"
          say="Staff Send needs a selected tenant (?owner=). Fleet runs stay empty until the backend sees that owner — not a hidden list."/>`
      : null}

    <div className="ops-hud__meters ops-send__meters" role="group" aria-label="Send meters">
      <div className="ops-hud__meter" data-on=${ready.length ? "1" : "0"} data-tone="go">
        <b>${readyV}</b><em>Free</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${ready.length ? 1 : 0.2})` }}/>
      </div>
      <div className="ops-hud__meter" data-on=${busy.length ? "1" : "0"} data-tone="warn">
        <b>${outV}</b><em>On run</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${busy.length ? 1 : 0.2})` }}/>
      </div>
      <div className="ops-hud__meter" data-on=${roster.length ? "1" : "0"} data-tone="go">
        <b>${totalV}</b><em>People</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${roster.length ? 1 : 0.2})` }}/>
      </div>
      <div className="ops-hud__meter" data-on=${openMissions.length ? "1" : "0"} data-tone="warn">
        <b>${runsV}</b><em>Open runs</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${openMissions.length ? 1 : 0.2})` }}/>
      </div>
    </div>

    <p className="ops-send__rule">
      Roster and select · pick a person to send. The assignment stays on their card.
      ${peopleSource === "soft" ? " · Practice people only on this machine" : ""}
    </p>

    <div className="ops-seg ops-alerts__tabs" role="tablist" aria-label="Send panes">
      ${SEND_TABS.map((t) => html`
        <button key=${t.id} type="button" role="tab" className="ops-seg__btn"
          data-on=${tab === t.id ? "1" : "0"}
          title=${t.long}
          onClick=${() => setTab(t.id)}>${t.label}</button>`)}
    </div>

    ${tab === "people" && peopleDenied
      ? html`<${Alert} tone="bad" title="People closed" say=${softDispatchErrorOf(peopleR.error)}/>`
      : null}
    ${tab === "people" && peopleR.error && !peopleDenied
      ? html`<${Alert} tone="warn" title="People unavailable" say=${softDispatchErrorOf(peopleR.error)}/>`
      : null}
    ${tab === "missions" && missionDenied
      ? html`<${Alert} tone="bad" title="Runs closed" say=${softDispatchErrorOf(missionPack.error)}/>`
      : null}
    ${tab === "missions" && missionPack.error && !missionDenied
      ? html`<${Alert} tone="warn" title="Runs unavailable" say=${softDispatchErrorOf(missionPack.error)}/>`
      : null}

    <section className="ops-send__roster ops-dispatch__roster ops-cmd__roster" aria-label="Send stage">
      ${tab === "people" ? html`
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>People · ${ready.length} free</p>
          <em className="ops-send__filter">${peopleSource === "live" ? "On site" : peopleSource === "soft" ? "Practice local" : "empty"}</em>
        </div>
        <div className="ops-send__list ops-cmd__roster-list" role="list">
          ${!seat.personnelAllowed && !softState
            ? html`<div className="ops-hud__quiet"><b>People closed</b> · Not available for this seat.</div>`
            : peopleR.loading && !peopleR.data && !softRoster.length
              ? html`<p className="ops-hud__quiet">Loading…</p>`
              : !roster.length
                ? html`<div className="ops-hud__quiet">
                    <b>No people on Send</b>
                    · Add staff under Dispatch → People, or open Command on a lab PC for practice mode.
                  </div>`
                : roster.map((r) => html`
                    <button
                      key=${r.id}
                      type="button"
                      role="listitem"
                      className="ops-send__unit ops-cmd__unit"
                      data-how=${r.how}
                      data-free=${r.free ? "1" : "0"}
                      data-live=${r.live ? "1" : "0"}
                      onClick=${() => {
                        if (r.soft) {
                          focusSoftUnit(r.id);
                          return;
                        }
                        if (!seat.unitSelectAllowed) {
                          toast && toast("Select is closed for this seat", "warn");
                          return;
                        }
                        if (r.live || r.kind === "person") {
                          if (onPerson) {
                            onPerson(r.id, "assign");
                            toast && toast("Person card open. Assign on the card.", "go");
                          } else if (onSelect) onSelect(r.id);
                          return;
                        }
                        if (onSelect) onSelect(r.id);
                      }}>
                      <i className="ops-send__unit-dot" aria-hidden="true" data-free=${r.free ? "1" : "0"} />
                      <span className="ops-send__unit-main">
                        <b>${r.label}</b>
                        <u>${r.kindWord}${r.meta ? " · " + r.meta : ""}</u>
                      </span>
                      <em className="ops-send__unit-st">${r.free ? "FREE" : String(r.how || "busy").toUpperCase()}</em>
                    </button>`)}
        </div>
      ` : null}

      ${tab === "missions" ? html`
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>Runs · ${openMissions.length} open</p>
          <em className="ops-send__filter">${seat.missionsFleetAllowed ? "site fleet" : "my queue"}</em>
        </div>
        <div className="ops-send__list ops-cmd__roster-list" role="list">
          ${!seat.missionsAllowed
            ? html`<div className="ops-hud__quiet"><b>Runs closed</b> · Not available for this seat.</div>`
            : missionPack.loading && !missionPack.data
              ? html`<p className="ops-hud__quiet">Loading…</p>`
              : !missions.length
                ? html`<div className="ops-hud__quiet">
                    <b>No runs yet</b>
                    · Create assignments from Dispatch → Send when a ticket opens.
                  </div>`
                : missions.map((m) => html`
                    <button
                      key=${m.id}
                      type="button"
                      role="listitem"
                      className="ops-send__unit ops-cmd__unit"
                      data-free=${m.open ? "0" : "1"}
                      data-how=${m.status}
                      onClick=${() => {
                        if (m.who && m.who !== "—" && seat.unitSelectAllowed) {
                          if (onPerson) onPerson(m.who);
                          else if (onSelect) onSelect(m.who);
                        } else {
                          toast && toast(
                            m.open ? ("Run · " + operatorSay(m.status, "Open")) : ("Closed · " + operatorSay(m.status, "Closed")),
                            "go",
                          );
                        }
                      }}>
                      <i className="ops-send__unit-dot" aria-hidden="true" data-free=${m.open ? "0" : "1"} />
                      <span className="ops-send__unit-main">
                        <b>${m.who}</b>
                        <u>${m.device ? boardHeadingOf(m.device) + " · " : ""}${operatorSay(m.reason, "On this run")}</u>
                      </span>
                      <em className="ops-send__unit-st">${operatorSay(m.status, "Open")}</em>
                    </button>`)}
        </div>
        <p className="ops-alerts__note">Open a person on People for Live assign. This Send desk stays roster and select.</p>
      ` : null}
    </section>

    <footer className="ops-hud__foot ops-send__foot">
      <span className="ops-hud__foot-role" data-soft=${peopleSource === "soft" ? "1" : "0"}>
        ${roleLabel}
      </span>
      <button type="button" className="btn btn--ghost btn--xs" onClick=${() => go("/dispatch/people")}>People book</button>
    </footer>
  </div>`;
}
